import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import axios from "axios";
import { MapContainer, TileLayer, Circle, CircleMarker, Popup, Polygon, Rectangle, Marker, Tooltip as LeafletTooltip } from "react-leaflet";
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { Buildings, Crosshair, ChartBar, GearSix, SignOut, CurrencyCircleDollar, UserCircle, CaretDown, Wallet, GearFine, Lock, Question } from "@phosphor-icons/react";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import LoginPage from "./LoginPage";
import AdminPage from "./AdminPage";
import AccountSettingsModal from "./AccountSettingsModal";
import UserGuidePage from "./UserGuidePage";
import {
  API, SA_CENTER, DEFAULT_ZOOM, MapClickHandler, MapCenterUpdater,
  createFacilityIcon, createCompetitorIcon, createMallIcon,
  formatZAR, getOiHeatmapColor, FACILITY_COLORS, getLsmColor, TRADE_ZONE_COLORS
} from "./mapConstants";
import { Sidebar } from "./Sidebar";
import { ScoutPanel } from "./ScoutPanel";
import { SpatialTab } from "./SpatialTab";
import { ModelStatsTab } from "./ModelStatsTab";

axios.defaults.withCredentials = true;

const pulsingIcon = L.divIcon({
  className: 'pulsing-marker',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -11],
  html: '<div class="pulse-pin"></div>'
});

function getOiInterpretation(score) {
  if (score >= 8) return "Excellent — very low competition, high demand, strong growth signals. Prime location for retail.";
  if (score >= 6) return "Good — favorable market conditions with manageable competition. Solid opportunity for a spaza shop.";
  if (score >= 4) return "Moderate — average market with some challenges. Consider competitive positioning carefully.";
  if (score >= 2) return "Below average — high competition or limited demand. Viability requires strong differentiation.";
  return "Low — saturated or declining market. High risk for new retail entrants.";
}

function App() {
  const { user, logout, isAdmin } = useAuth();
  const [showAdmin, setShowAdmin] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);

  const [address, setAddress] = useState("");
  const [useCoordinates, setUseCoordinates] = useState(false);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  const [bedrooms, setBedrooms] = useState(null);
  const [bathrooms, setBathrooms] = useState(null);
  const [hasSecurity, setHasSecurity] = useState(false);
  const [hasPool, setHasPool] = useState(false);
  const [hasDstv, setHasDstv] = useState(false);
  const [propertySize, setPropertySize] = useState(null);

  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [userCredits, setUserCredits] = useState(null);

  const [heatmapData, setHeatmapData] = useState([]);
  const [variogramData, setVariogramData] = useState([]);
  const [rentCircles, setRentCircles] = useState(null);
  const [mapCenter, setMapCenter] = useState(null);
  const [metros, setMetros] = useState([]);
  const [oiHeatmap, setOiHeatmap] = useState([]);
  const [showOiHeatmap, setShowOiHeatmap] = useState(false);
  const [loadingOiHeatmap, setLoadingOiHeatmap] = useState(false);

  const [dataHealth, setDataHealth] = useState(null);
  const [scraping, setScraping] = useState(false);
  const [importing, setImporting] = useState(false);
  const [oiModelFit, setOiModelFit] = useState(null);

  const [activeTab, setActiveTab] = useState("map");
  const [resolvedAddress, setResolvedAddress] = useState("");
  const [installPrompt, setInstallPrompt] = useState(null);

  const [scoutLayers, setScoutLayers] = useState(null);
  const [loadingScout, setLoadingScout] = useState(false);
  const [municipalityData, setMunicipalityData] = useState(null);
  const [showMuniPanel, setShowMuniPanel] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [oiHoneycomb, setOiHoneycomb] = useState([]);
  const [marketFitPatches, setMarketFitPatches] = useState([]);
  const [activeCircleIdx, setActiveCircleIdx] = useState(null);
  const [scoutCollapsed, setScoutCollapsed] = useState(false);
  const [layerToggles, setLayerToggles] = useState({
    facilities: true,
    competitors: false,
    population: true,
    lsmZones: false,
    tradeArea: false,
    huffModel: false,
    oiHoneycomb: false,
    marketFit: true,
    rentHeatmap: false,
    comparables: true,
    rentCircleColors: false,
  });

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    fetchMetros();
    fetchVariogram();
    fetchDataHealth();
    fetchCredits();
  }, []);

  // Fetch credits when user changes (login/register)
  useEffect(() => {
    if (user && user !== false) {
      // If user object has credits, use it directly
      if (user.credits !== undefined) {
        setUserCredits(user.credits);
      } else {
        // Otherwise fetch from API
        fetchCredits();
      }
    }
  }, [user]);

  const fetchCredits = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      // Default to 0 if credits is undefined (for users created before credits field was added)
      setUserCredits(response.data.credits !== undefined ? response.data.credits : 0);
    } catch (error) {
      // Not critical
    }
  };

  const fetchMetros = async () => {
    try {
      const response = await axios.get(`${API}/metros`);
      setMetros(response.data);
    } catch (error) {
      console.error("Error fetching metros:", error);
    }
  };

  const fetchVariogram = async () => {
    try {
      const response = await axios.get(`${API}/variogram`);
      setVariogramData(response.data);
    } catch (error) {
      console.error("Error fetching variogram:", error);
    }
  };

  const fetchRentCircles = async (lat, lng) => {
    try {
      const response = await axios.get(`${API}/rent-circles`, { params: { lat, lng } });
      setRentCircles(response.data);
    } catch (error) {
      console.error("Error fetching rent circles:", error);
    }
  };

  const fetchScoutLayers = async (lat, lng) => {
    setLoadingScout(true);
    try {
      const response = await axios.get(`${API}/spatial/layers`, { params: { lat, lng } });
      setScoutLayers(response.data);

      if (layerToggles.oiHoneycomb) {
        const hcRes = await axios.get(`${API}/spatial/oi-honeycomb`, { params: { lat, lng } });
        setOiHoneycomb(hcRes.data.cells || []);
      }
      if (layerToggles.marketFit) {
        const mfRes = await axios.get(`${API}/spatial/market-fit`, { params: { lat, lng, radius_km: 3 } });
        setMarketFitPatches(mfRes.data.patches || []);
      }
    } catch (error) {
      console.error("Error fetching spatial layers:", error);
    } finally {
      setLoadingScout(false);
    }
  };

  const toggleLayer = async (key) => {
    if (!isAdmin && userCredits !== null && userCredits < 1) {
      toast.error('Insufficient credits. Purchase credits to access map layers.');
      return;
    }
    const newVal = !layerToggles[key];
    setLayerToggles(prev => ({ ...prev, [key]: newVal }));
    if (newVal && latitude && longitude) {
      if (key === 'oiHoneycomb' && oiHoneycomb.length === 0) {
        try {
          const response = await axios.get(`${API}/spatial/oi-honeycomb`, { params: { lat: latitude, lng: longitude } });
          setOiHoneycomb(response.data.cells || []);
          toast.success(`Nkosi's Opportunity Index loaded: ${response.data.count} cells`);
        } catch (error) {
          console.error("Nkosi's Opportunity Index honeycomb failed:", error);
        }
      }
      if (key === 'marketFit' && marketFitPatches.length === 0) {
        try {
          const response = await axios.get(`${API}/spatial/market-fit`, { params: { lat: latitude, lng: longitude, radius_km: 3 } });
          setMarketFitPatches(response.data.patches || []);
          toast.success(`Market Fit loaded: ${response.data.count} patches`);
        } catch (error) {
          console.error("Market fit failed:", error);
        }
      }
    } else if (newVal && !latitude) {
      toast.info("Run a valuation first to load this layer");
    }
  };

  const handleRightClick = (lat, lng, event) => {
    setContextMenu({ x: event?.originalEvent?.clientX || 300, y: event?.originalEvent?.clientY || 300, lat, lng });
  };

  const handleContextMenuAction = async (action) => {
    if (!contextMenu) return;
    if (!isAdmin && userCredits !== null && userCredits < 1) {
      toast.error('Insufficient credits. Purchase credits to access spatial layers.');
      setContextMenu(null);
      return;
    }
    const lat = latitude || contextMenu.lat;
    const lng = longitude || contextMenu.lng;
    setContextMenu(null);

    if (action === "municipality") {
      try {
        const response = await axios.get(`${API}/spatial/municipality`, { params: { lat, lng } });
        if (response.data.found) {
          setMunicipalityData(response.data);
          setShowMuniPanel(true);
        } else {
          toast.info("No municipality boundary data found for this location");
        }
      } catch (error) {
        console.error("Municipality lookup failed:", error);
      }
    } else if (action === "oi_honeycomb") {
      try {
        const response = await axios.get(`${API}/spatial/oi-honeycomb`, { params: { lat, lng } });
        setOiHoneycomb(response.data.cells || []);
        setLayerToggles(prev => ({ ...prev, oiHoneycomb: true }));
        toast.success(`Nkosi's Opportunity Index loaded: ${response.data.count} cells`);
      } catch (error) {
        console.error("Nkosi's Opportunity Index honeycomb failed:", error);
      }
    } else if (action === "market_fit") {
      try {
        const response = await axios.get(`${API}/spatial/market-fit`, { params: { lat, lng, radius_km: 3 } });
        setMarketFitPatches(response.data.patches || []);
        setLayerToggles(prev => ({ ...prev, marketFit: true }));
        toast.success(`Market Fit loaded: ${response.data.count} patches`);
      } catch (error) {
        console.error("Market fit failed:", error);
      }
    }
  };

  const fetchDataHealth = async () => {
    try {
      const response = await axios.get(`${API}/data-health`);
      setDataHealth(response.data);
    } catch (error) {
      console.error("Error fetching data health:", error);
    }
  };

  const fetchOiModelFit = async () => {
    try {
      const response = await axios.get(`${API}/opportunity-model-fit`);
      setOiModelFit(response.data);
    } catch (error) {
      console.error("Error fetching OI model fit:", error);
    }
  };

  const handleScrapeData = async () => {
    setScraping(true);
    try {
      await axios.post(`${API}/scrape-data`);
      toast.info("Scraping Property24 & PrivateProperty...");

      const pollInterval = setInterval(async () => {
        const response = await axios.get(`${API}/data-health`);
        setDataHealth(response.data);
        if (response.data.scraping_status === "complete" || response.data.scraping_status === "error") {
          clearInterval(pollInterval);
          setScraping(false);
          if (response.data.scraping_status === "complete") {
            toast.success(`Data updated! ${response.data.total_properties} properties from live sources.`);
          } else {
            toast.error("Scraping encountered issues — check logs.");
          }
          fetchVariogram();
        }
      }, 3000);
    } catch (error) {
      setScraping(false);
      toast.error("Error starting data scraping");
    }
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { inserted, skipped, geocoded, total_properties } = response.data;
      toast.success(`Imported ${inserted} properties (${skipped} skipped${geocoded ? `, ${geocoded} geocoded` : ''}). Total: ${total_properties}`);

      const dhResponse = await axios.get(`${API}/data-health`);
      setDataHealth(dhResponse.data);
      fetchVariogram();
    } catch (error) {
      const msg = error.response?.data?.detail || "Error importing file";
      toast.error(msg);
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleMapClick = useCallback((lat, lng) => {
    setContextMenu(null);
    setLatitude(parseFloat(lat.toFixed(4)));
    setLongitude(parseFloat(lng.toFixed(4)));
    setUseCoordinates(true);
    setResolvedAddress("");
    setMapCenter([lat, lng]);
    toast.info(`Location pinned: ${lat.toFixed(4)}, ${lng.toFixed(4)}`);
  }, []);

  const handleNewValuation = () => {
    setAddress("");
    setLatitude(null);
    setLongitude(null);
    setUseCoordinates(false);
    setBedrooms(null);
    setBathrooms(null);
    setHasSecurity(false);
    setHasPool(false);
    setHasDstv(false);
    setPropertySize(null);
    setPrediction(null);
    setResolvedAddress("");
    setRentCircles(null);
    setActiveCircleIdx(null);
    setMapCenter(null);
    setScoutLayers(null);
    setOiHoneycomb([]);
    setMarketFitPatches([]);
    setMunicipalityData(null);
    setShowMuniPanel(false);
    setContextMenu(null);
    toast.info("Ready for new valuation");
  };

  const handlePredict = async () => {
    const hasValidCoordinates = latitude !== null && longitude !== null;
    const hasValidAddress = address.trim().length > 0;

    if (!hasValidCoordinates && !hasValidAddress) {
      toast.error("Please enter an address or click on the map");
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        has_security: hasSecurity,
        has_pool: hasPool,
        has_dstv: hasDstv,
      };

      requestData.bedrooms = bedrooms !== null ? bedrooms : 1;
      requestData.bathrooms = bathrooms !== null ? bathrooms : 1;

      if (propertySize !== null && propertySize > 0) {
        requestData.property_size_sqm = propertySize;
      }

      if (useCoordinates && hasValidCoordinates) {
        requestData.latitude = latitude;
        requestData.longitude = longitude;
      } else if (hasValidAddress) {
        requestData.address = address;
      } else if (hasValidCoordinates) {
        requestData.latitude = latitude;
        requestData.longitude = longitude;
      }

      const response = await axios.post(`${API}/predict`, requestData);
      setPrediction(response.data);

      if (response.data.resolved_address) {
        setResolvedAddress(response.data.resolved_address);
      }

      const respLat = response.data.latitude;
      const respLng = response.data.longitude;

      if (respLat && respLng) {
        setLatitude(respLat);
        setLongitude(respLng);
        setMapCenter([respLat, respLng]);

        setOiHoneycomb([]);
        setMarketFitPatches([]);
        setHeatmapData([]);

        await Promise.all([
          fetchRentCircles(respLat, respLng),
          fetchScoutLayers(respLat, respLng),
        ]);
      }

      toast.success("Valuation complete!");
      fetchCredits();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error calculating valuation";
      toast.error(errorMsg);
      console.error("Prediction error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (reportType) => {
    if (!prediction) {
      toast.error("Please run a valuation first");
      return;
    }

    setGeneratingPdf(true);
    try {
      const requestData = {
        has_security: hasSecurity,
        has_pool: hasPool,
        has_dstv: hasDstv,
      };

      if (bedrooms !== null) requestData.bedrooms = bedrooms;
      if (bathrooms !== null) requestData.bathrooms = bathrooms;

      if (latitude && longitude) {
        requestData.latitude = latitude;
        requestData.longitude = longitude;
      }
      if (address) {
        requestData.address = address;
      }
      if (resolvedAddress) {
        requestData.resolved_address = resolvedAddress;
      }

      const endpoint = reportType === "property" ? "generate-property-report" : "generate-site-report";
      const filename = reportType === "property" ? "Nkosis_Property_Valuation_Report.pdf" : "Nkosis_Site_Valuation_Report.pdf";

      const response = await axios.post(`${API}/${endpoint}`, requestData, { responseType: "blob" });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(`${reportType === "property" ? "Property" : "Site"} report downloaded!`);
    } catch (error) {
      toast.error("Error generating PDF report");
      console.error("PDF generation error:", error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadManual = async () => {
    try {
      const response = await axios.get(`${API}/download-manual`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Cokriging_Desktop_Valuator_Manual.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Manual downloaded!");
    } catch (error) {
      toast.error("Error downloading manual");
    }
  };

  const handleMetroSelect = (metro) => {
    setLatitude(metro.lat);
    setLongitude(metro.lng);
    setUseCoordinates(true);
    setMapCenter([metro.lat, metro.lng]);
    setResolvedAddress(`${metro.name}, South Africa`);
    setAddress(metro.name);
    toast.info(`Selected ${metro.name}`);
  };

  const coefficientData = prediction?.model_statistics?.coefficients
    ? Object.entries(prediction.model_statistics.coefficients)
        .filter(([key]) => key !== "intercept" && key !== "latitude" && key !== "longitude")
        .map(([key, value]) => ({
          name: key.replace(/ln_/g, "ln(").replace(/_/g, " ") + (key.startsWith("ln_") ? ")" : ""),
          value: value,
          fill: value > 0 ? "#10b981" : "#ef4444",
        }))
    : [];

  // Auth gates
  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-slate-900"><div className="text-white">Loading...</div></div>;
  if (user === false) return <><Toaster position="top-right" richColors /><LoginPage /></>;
  if (showAdmin && isAdmin) return <><Toaster position="top-right" richColors /><AdminPage onBack={() => setShowAdmin(false)} /></>;

  return (
    <div className="app-container" data-testid="app-container">
      <Toaster position="top-right" richColors />

      <Sidebar
        address={address} setAddress={setAddress} setUseCoordinates={setUseCoordinates}
        handlePredict={handlePredict} resolvedAddress={resolvedAddress}
        useCoordinates={useCoordinates} latitude={latitude} longitude={longitude}
        metros={metros} handleMetroSelect={handleMetroSelect}
        bedrooms={bedrooms} setBedrooms={setBedrooms}
        bathrooms={bathrooms} setBathrooms={setBathrooms}
        hasSecurity={hasSecurity} setHasSecurity={setHasSecurity}
        hasPool={hasPool} setHasPool={setHasPool}
        hasDstv={hasDstv} setHasDstv={setHasDstv}
        propertySize={propertySize} setPropertySize={setPropertySize}
        prediction={prediction}
        dataHealth={dataHealth} scraping={scraping} handleScrapeData={handleScrapeData}
        importing={importing} handleExcelImport={handleExcelImport}
        isAdmin={isAdmin} loading={loading} generatingPdf={generatingPdf}
        handleDownloadReport={handleDownloadReport}
        handleNewValuation={handleNewValuation}
        handleDownloadManual={handleDownloadManual}
      />

      <main className="main-content-light" data-testid="main-content">
        <header className="header-light">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-slate-800">Automated Valuation Model</h2>
            <span className="text-xs uppercase tracking-widest text-slate-400 font-medium">South Africa</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex border border-slate-200 rounded overflow-hidden">
              <button
                onClick={() => setActiveTab("map")}
                className={`px-4 py-2 text-xs font-medium transition-colors ${activeTab === "map" ? "bg-slate-800 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                data-testid="tab-map"
              >
                Map View
              </button>
              <button
                onClick={() => setActiveTab("spatial")}
                className={`px-4 py-2 text-xs font-medium border-l border-slate-200 transition-colors ${activeTab === "spatial" ? "bg-slate-800 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                data-testid="tab-spatial"
              >
                Spatial Intelligence
              </button>
              {isAdmin && (
                <button
                  onClick={() => setActiveTab("stats")}
                  className={`px-4 py-2 text-xs font-medium border-l border-slate-200 transition-colors ${activeTab === "stats" ? "bg-slate-800 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
                  data-testid="tab-stats"
                >
                  Model Stats
                </button>
              )}
            </div>
            <div className="flex items-center gap-2 ml-2 pl-2 border-l border-slate-200">
              {!isAdmin && userCredits !== null && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid="credit-balance">
                  <CurrencyCircleDollar size={14} weight="fill" />
                  {userCredits} credits
                </span>
              )}
              {/* Profile Dropdown */}
              <div className="relative" data-testid="profile-dropdown-container">
                <button
                  onClick={() => setProfileOpen(prev => !prev)}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-md hover:bg-slate-100 transition-colors"
                  data-testid="profile-dropdown-trigger"
                >
                  <UserCircle size={20} weight="fill" className="text-slate-500" />
                  <span className="text-xs text-slate-600 font-medium max-w-[120px] truncate">{user?.name || user?.email}</span>
                  <CaretDown size={12} className={`text-slate-400 transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                </button>
                {profileOpen && (
                  <>
                    <div className="fixed inset-0 z-[1500]" onClick={() => setProfileOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-[1501] bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[200px]" data-testid="profile-dropdown-menu">
                      <div className="px-3 py-2 border-b border-slate-100">
                        <div className="text-sm font-semibold text-slate-800">{user?.name || 'User'}</div>
                        <div className="text-xs text-slate-400">{user?.email}</div>
                      </div>
                      {isAdmin && (
                        <button
                          onClick={() => { setProfileOpen(false); setShowAdmin(true); }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2 transition-colors"
                          data-testid="menu-user-management"
                        >
                          <GearSix size={16} className="text-slate-500" />
                          User Management
                        </button>
                      )}
                      {!isAdmin && (
                        <div className="px-3 py-2">
                          <button
                            onClick={() => { setProfileOpen(false); setShowAccountSettings(true); }}
                            className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-md flex items-center justify-center gap-1.5 transition-colors"
                            data-testid="menu-buy-credits"
                          >
                            <Wallet size={14} weight="bold" />
                            Buy Credits
                          </button>
                        </div>
                      )}
                      {!isAdmin && (
                        <button
                          onClick={() => { setProfileOpen(false); setShowAccountSettings(true); }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2 transition-colors"
                          data-testid="menu-account-settings"
                        >
                          <GearFine size={16} className="text-slate-500" />
                          Account Settings
                        </button>
                      )}
                      {!isAdmin && (
                        <button
                          onClick={() => { setProfileOpen(false); setShowUserGuide(true); }}
                          className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2 transition-colors"
                          data-testid="menu-user-guide"
                        >
                          <Question size={16} className="text-slate-500" />
                          User Guide
                        </button>
                      )}
                      <div className="border-t border-slate-100">
                        <button
                          onClick={() => { setProfileOpen(false); logout(); }}
                          className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                          data-testid="menu-logout"
                        >
                          <SignOut size={16} />
                          Sign Out
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <div className="metrics-row-light" data-testid="metrics-row">
          <div className="metric-item-light">
            <span className="metric-label-light">Estimated Monthly Rent</span>
            <span className="metric-value-light" data-testid="rent-value">
              {prediction ? formatZAR(prediction.estimated_rent) : "\u2014"}
            </span>
            {prediction && (
              <span className="text-xs text-slate-400 mt-1">
                Range: {formatZAR(prediction.confidence_low)} - {formatZAR(prediction.confidence_high)}
              </span>
            )}
          </div>
          <div className="metric-item-light">
            <span className="metric-label-light">Property Value</span>
            <span className="metric-value-light" data-testid="value-value">
              {prediction ? formatZAR(prediction.estimated_value) : "\u2014"}
            </span>
            {prediction && (
              <span className="text-xs text-slate-400 mt-1">
                {prediction.bedrooms_used} bed, {prediction.bathrooms_used} bath
              </span>
            )}
          </div>
          <div className="metric-item-light">
            <span className="metric-label-light">Location Score</span>
            <span className="metric-value-light" data-testid="score-value">
              {prediction ? `${prediction.location_quality_score}/10` : "\u2014"}
            </span>
            {prediction?.model_statistics && (
              <span className="text-xs text-emerald-600 mt-1">
                R2 = {prediction.model_statistics.r_squared}
              </span>
            )}
          </div>
          <div className="metric-item-light border-r-0">
            <span className="metric-label-light">{"Nkosi's Opportunity Index"}</span>
            <span className="metric-value-light" data-testid="opportunity-value" style={{color: prediction?.opportunity_index?.opportunity_index >= 6 ? '#059669' : prediction?.opportunity_index?.opportunity_index >= 4 ? '#d97706' : '#dc2626'}}>
              {prediction?.opportunity_index ? `${prediction.opportunity_index.opportunity_index}/10` : "\u2014"}
            </span>
            {prediction?.opportunity_index && (
              <span className="text-xs text-slate-400 mt-1">
                {prediction.opportunity_index.breakdown?.territory?.supply_gap || "\u2014"} market
              </span>
            )}
          </div>
        </div>

        {activeTab === "map" ? (
          <>
            <div className="map-container-light relative" data-testid="map-container">
              <MapContainer
                center={SA_CENTER}
                zoom={DEFAULT_ZOOM}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <MapClickHandler onMapClick={handleMapClick} onRightClick={handleRightClick} />
                {mapCenter && <MapCenterUpdater center={mapCenter} />}

                {rentCircles && latitude && longitude && [...rentCircles.circles].reverse().map((circle, revIdx) => {
                  const idx = rentCircles.circles.length - 1 - revIdx;
                  const tradeAreaActive = layerToggles.tradeArea && scoutLayers?.trade_area?.zones?.length > 0;
                  return (
                    <Circle
                      key={`circle-${idx}`}
                      center={[latitude, longitude]}
                      radius={circle.radius}
                      interactive={!tradeAreaActive}
                      pathOptions={{
                        color: activeCircleIdx === idx ? "#0a2463" : "#1e3a5f",
                        fillColor: layerToggles.rentCircleColors ? "#1e3a5f" : "transparent",
                        fillOpacity: layerToggles.rentCircleColors ? 0.04 * (5 - idx) : 0,
                        weight: activeCircleIdx === idx ? 2.5 : 1,
                        dashArray: idx > 0 ? "6, 4" : undefined,
                        pointerEvents: tradeAreaActive ? 'none' : 'auto',
                      }}
                      eventHandlers={tradeAreaActive ? {} : {
                        click: (e) => {
                          e.originalEvent?.stopPropagation?.();
                          setActiveCircleIdx(idx);
                        },
                      }}
                    >
                      {!tradeAreaActive && (
                        <LeafletTooltip sticky direction="top" offset={[0, -10]} className="radius-tooltip">
                          <div style={{fontSize: '12px', lineHeight: '1.5', minWidth: '140px'}}>
                            <strong>{circle.radius_km} km from site</strong><br />
                            Avg Rent: {formatZAR(circle.avg_rent)}<br />
                            Change: <span style={{color: circle.rent_change >= 0 ? '#059669' : '#dc2626', fontWeight: 600}}>{circle.rent_change > 0 ? "+" : ""}{circle.rent_change}%</span>
                          </div>
                        </LeafletTooltip>
                      )}
                    </Circle>
                  );
                })}

                {rentCircles && activeCircleIdx !== null && rentCircles.circles[activeCircleIdx] && latitude && longitude && !(layerToggles.tradeArea && scoutLayers?.trade_area?.zones?.length > 0) && (() => {
                  const c = rentCircles.circles[activeCircleIdx];
                  return (
                    <CircleMarker
                      center={[latitude + (c.radius / 1000) / 111, longitude]}
                      radius={0}
                      pathOptions={{ opacity: 0, fillOpacity: 0 }}
                    >
                      <LeafletTooltip permanent direction="right" offset={[6, 0]} className="radius-badge-tooltip">
                        <div style={{fontSize: '11px', lineHeight: '1.4', padding: '2px 4px'}}>
                          <strong style={{color: '#0a2463'}}>{c.radius_km} km</strong> | Avg: {formatZAR(c.avg_rent)} | <span style={{color: c.rent_change >= 0 ? '#059669' : '#dc2626'}}>{c.rent_change > 0 ? '+' : ''}{c.rent_change}%</span>
                        </div>
                      </LeafletTooltip>
                    </CircleMarker>
                  );
                })()}

                {latitude && longitude && (
                  <Marker
                    position={[latitude, longitude]}
                    icon={pulsingIcon}
                    zIndexOffset={1000}
                  >
                    {prediction?.opportunity_index && (
                      <LeafletTooltip direction="top" offset={[0, -14]} className="oi-pin-tooltip">
                        <div style={{fontSize: '12px', lineHeight: '1.5', maxWidth: '280px'}}>
                          <strong>{"Nkosi's Opportunity Index:"} {prediction.opportunity_index.opportunity_index}/10</strong>
                          <div style={{fontSize: '11px', color: '#94a3b8', marginTop: '3px'}}>{getOiInterpretation(prediction.opportunity_index.opportunity_index)}</div>
                        </div>
                      </LeafletTooltip>
                    )}
                    <Popup>
                      <div className="text-sm">
                        <strong>Selected Location</strong><br />
                        {resolvedAddress && <>{resolvedAddress.substring(0, 40)}...<br /></>}
                        Lat: {latitude?.toFixed(4)}, Lng: {longitude?.toFixed(4)}
                        {prediction && (
                          <>
                            <br /><br />
                            <strong>Rent:</strong> {formatZAR(prediction.estimated_rent)}
                          </>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                )}

                {layerToggles.comparables && prediction?.comparables?.map((comp, idx) => (
                  comp.latitude && comp.longitude ? (
                    <CircleMarker
                      key={`comp-${idx}`}
                      center={[comp.latitude, comp.longitude]}
                      radius={8}
                      fillColor="#0a2463"
                      fillOpacity={0.9}
                      color="#FFFFFF"
                      weight={2}
                    >
                      <Popup>
                        <div className="text-sm">
                          <strong>Comparable #{idx + 1}</strong><br />
                          {comp.city}<br />
                          <strong>Rent:</strong> {formatZAR(comp.rent)}<br />
                          {comp.bedrooms} bed / {comp.bathrooms} bath<br />
                          {comp.distance_km} km from target
                          {comp.has_security && <><br />Security</>}
                          {comp.has_pool && <><br />Pool</>}
                        </div>
                      </Popup>
                    </CircleMarker>
                  ) : null
                ))}

                {layerToggles.oiHoneycomb && oiHoneycomb.map((cell, idx) => {
                  const cellInterp = cell.oi_score >= 7.5 ? 'Prime location — minimal competition, strong demand signals.'
                    : cell.oi_score >= 6.0 ? 'Strong potential with good market indicators.'
                    : cell.oi_score >= 4.5 ? 'Moderate opportunity — weigh competitive factors.'
                    : cell.oi_score >= 3.0 ? 'Below average — challenges outweigh opportunities.'
                    : 'Low viability — high saturation or weak demand.';
                  return (
                    <Polygon
                      key={`oi-hex-${idx}`}
                      positions={cell.polygon}
                      pathOptions={{
                        fillColor: getOiHeatmapColor(cell.oi_score),
                        fillOpacity: 0.55,
                        color: '#ffffff',
                        weight: 0.5,
                        opacity: 0.4,
                      }}
                    >
                      <LeafletTooltip direction="top" sticky className="interp-tooltip">
                        <div className="text-xs" style={{maxWidth: '280px'}}>
                          <strong>{"Nkosi's Opportunity Index:"} {cell.oi_score}/10</strong>
                          <div style={{marginTop: '3px', color: '#475569', lineHeight: '1.4'}}>{cellInterp}</div>
                        </div>
                      </LeafletTooltip>
                      <Popup>
                        <div className="text-sm">
                          <strong>{"Nkosi's Opportunity Index:"} {cell.oi_score}/10</strong><br />
                          <span className="text-xs">{cellInterp}</span><br />
                          <span className="text-xs text-slate-400">{cell.center[0].toFixed(4)}, {cell.center[1].toFixed(4)}</span>
                        </div>
                      </Popup>
                    </Polygon>
                  );
                })}

                {layerToggles.marketFit && marketFitPatches.map((patch, idx) => {
                  const color = patch.score >= 0.7 ? "#22c55e" : patch.score >= 0.5 ? "#eab308" : patch.score >= 0.3 ? "#f97316" : "#ef4444";
                  const label = patch.score >= 0.7 ? 'Excellent' : patch.score >= 0.5 ? 'Good' : patch.score >= 0.3 ? 'Fair' : 'Low';
                  const interp = patch.score >= 0.7 ? 'High demand, low competition — ideal for a spaza shop.'
                    : patch.score >= 0.5 ? 'Favorable conditions with moderate competition.'
                    : patch.score >= 0.3 ? 'Some potential but competitive pressure or lower demand.'
                    : 'Saturated area or low foot traffic — consider alternatives.';
                  return (
                    <Rectangle
                      key={`mfit-${idx}`}
                      bounds={patch.bounds}
                      pathOptions={{
                        fillColor: color,
                        fillOpacity: 0.35,
                        color: color,
                        weight: 0.3,
                        opacity: 0.2,
                      }}
                    >
                      <LeafletTooltip direction="top" sticky className="interp-tooltip">
                        <div className="text-xs" style={{maxWidth: '280px'}}>
                          <strong>Market Fit: {(patch.score * 100).toFixed(0)}% — {label}</strong>
                          <div style={{marginTop: '3px', color: '#475569', lineHeight: '1.4'}}>{interp}</div>
                        </div>
                      </LeafletTooltip>
                      <Popup>
                        <div className="text-sm">
                          <strong>Market Fit: {(patch.score * 100).toFixed(0)}%</strong><br />
                          <span className="text-xs">{label} — {interp}</span>
                        </div>
                      </Popup>
                    </Rectangle>
                  );
                })}

                {layerToggles.facilities && scoutLayers?.facilities?.map((f, idx) => (
                  <Marker
                    key={`facility-${idx}`}
                    position={[f.lat, f.lng]}
                    icon={createFacilityIcon(f.type)}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong style={{color: FACILITY_COLORS[f.type]}}>{f.type.charAt(0).toUpperCase() + f.type.slice(1)}</strong><br />
                        {f.name}<br />
                        <span className="text-xs">{f.distance_km} km away</span>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {layerToggles.competitors && scoutLayers?.competitors?.spaza_clusters?.map((s, idx) => (
                  <Marker
                    key={`spaza-${idx}`}
                    position={[s.lat, s.lng]}
                    icon={createCompetitorIcon(Math.min(36, 24 + s.estimated_shops / 25))}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong style={{color: '#f97316'}}>Spaza Cluster</strong><br />
                        {s.name}<br />
                        ~{s.estimated_shops} shops | {s.density_per_km2}/km2<br />
                        <span className="text-xs">{s.distance_km} km away | {s.area_type.replace(/_/g, ' ')}</span>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {layerToggles.competitors && scoutLayers?.competitors?.co_tenants?.map((t, idx) => (
                  <Marker
                    key={`tenant-${idx}`}
                    position={[t.lat, t.lng]}
                    icon={t.type === "mall" ? createMallIcon() : createCompetitorIcon(24)}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong style={{color: t.type === "mall" ? '#7c3aed' : '#0ea5e9'}}>{t.type === "mall" ? "Shopping Mall" : "Petrol Station"}</strong><br />
                        {t.name}<br />
                        Anchor: {t.anchor}<br />
                        <span className="text-xs">{t.distance_km} km | {t.size_sqm.toLocaleString()} m2</span>
                      </div>
                    </Popup>
                  </Marker>
                ))}

                {layerToggles.lsmZones && scoutLayers?.lsm?.wards_in_range?.map((w, idx) => (
                  <Circle
                    key={`lsm-${idx}`}
                    center={[w.lat, w.lng]}
                    radius={Math.min(4000, 1500 + w.population / 100)}
                    fillColor={getLsmColor(w.lsm_band)}
                    fillOpacity={0.3}
                    color={getLsmColor(w.lsm_band)}
                    weight={1.5}
                  >
                    <LeafletTooltip direction="top" sticky>
                      <div className="text-xs">
                        <strong>{w.name}</strong> — LSM {w.lsm_band}/10<br />
                        R{w.avg_household_income.toLocaleString()}/mo | Pop: {w.population.toLocaleString()}
                      </div>
                    </LeafletTooltip>
                    <Popup>
                      <div className="text-sm">
                        <strong>{w.name}</strong><br />
                        LSM Band: {w.lsm_band}/10<br />
                        Avg Income: R{w.avg_household_income.toLocaleString()}/mo<br />
                        Pop: {w.population.toLocaleString()}<br />
                        Unemployment: {w.unemployment_rate}%<br />
                        <span className="text-xs">{w.distance_km} km away</span>
                      </div>
                    </Popup>
                  </Circle>
                ))}

                {layerToggles.tradeArea && scoutLayers?.trade_area?.zones?.slice().sort((a, b) => b.minutes - a.minutes).map((zone, idx) => {
                  const style = TRADE_ZONE_COLORS[zone.zone_label] || TRADE_ZONE_COLORS.tertiary;
                  return (
                    <Polygon
                      key={`trade-zone-${idx}`}
                      positions={zone.polygon}
                      pathOptions={{
                        color: style.color,
                        fillColor: style.color,
                        fillOpacity: style.fillOpacity,
                        weight: 2,
                        dashArray: zone.zone_label === "tertiary" ? "8, 4" : zone.zone_label === "secondary" ? "5, 5" : undefined,
                      }}
                    >
                      <LeafletTooltip sticky direction="top" className="interp-tooltip">
                        <div className="text-xs" style={{maxWidth: '260px'}}>
                          <strong>{zone.minutes}-min walk ({zone.zone_label})</strong>
                          <div style={{marginTop: '3px', color: '#475569', lineHeight: '1.4'}}>
                            Radius: {(zone.radius_km * 1000).toFixed(0)}m &middot; Capture: {(zone.capture_pct * 100).toFixed(0)}% of customers<br/>
                            Est. population: {zone.estimated_population.toLocaleString()} &middot; Spaza clusters: {zone.spaza_clusters_inside}
                          </div>
                        </div>
                      </LeafletTooltip>
                      <Popup>
                        <div className="text-sm">
                          <strong>{zone.minutes}-min walk ({zone.zone_label})</strong><br />
                          Radius: {(zone.radius_km * 1000).toFixed(0)}m<br />
                          Capture: {(zone.capture_pct * 100).toFixed(0)}% of customers<br />
                          Spaza clusters: {zone.spaza_clusters_inside}<br />
                          Est. population: {zone.estimated_population.toLocaleString()}
                        </div>
                      </Popup>
                    </Polygon>
                  );
                })}

                {layerToggles.population && scoutLayers?.population_grid?.map((pt, idx) => (
                  <CircleMarker
                    key={`pop-${idx}`}
                    center={pt}
                    radius={2.5}
                    fillColor="#22c55e"
                    fillOpacity={0.65}
                    stroke={false}
                  />
                ))}

                {municipalityData?.boundary && showMuniPanel && (
                  <Polygon
                    positions={municipalityData.boundary}
                    pathOptions={{
                      color: "#0a2463",
                      fillColor: "#3b82f6",
                      fillOpacity: 0.08,
                      weight: 3,
                      dashArray: "6, 3",
                    }}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong>{municipalityData.name}</strong><br />
                        {municipalityData.province}<br />
                        Pop: {municipalityData.population?.toLocaleString()}
                      </div>
                    </Popup>
                  </Polygon>
                )}

                {metros.map((metro) => (
                  <CircleMarker
                    key={metro.name}
                    center={[metro.lat, metro.lng]}
                    radius={6}
                    fillColor="#1e3a5f"
                    fillOpacity={0.8}
                    color="#FFFFFF"
                    weight={2}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong>{metro.name}</strong><br />
                        Base Rent: {formatZAR(metro.base_rent)}
                      </div>
                    </Popup>
                  </CircleMarker>
                ))}
              </MapContainer>

              {contextMenu && (
                <div
                  className="fixed z-[2000] bg-white rounded-lg shadow-xl border border-slate-200 py-1 min-w-[200px]"
                  style={{ left: contextMenu.x, top: contextMenu.y, pointerEvents: 'auto' }}
                  data-testid="map-context-menu"
                >
                  <div className="px-3 py-1.5 text-xs text-slate-400 border-b border-slate-100">
                    {contextMenu.lat.toFixed(4)}, {contextMenu.lng.toFixed(4)}
                  </div>
                  <button
                    onClick={() => handleContextMenuAction("municipality")}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2 transition-colors"
                    data-testid="ctx-municipality"
                  >
                    <Buildings size={16} className="text-blue-500" />
                    Show Local Municipality Boundary
                  </button>
                  <button
                    onClick={() => handleContextMenuAction("oi_honeycomb")}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-2 transition-colors"
                    data-testid="ctx-oi-honeycomb"
                  >
                    <Crosshair size={16} className="text-emerald-500" />
                    {"Nkosi's Opportunity Index (1,25km)"}
                  </button>
                  <button
                    onClick={() => handleContextMenuAction("market_fit")}
                    className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-amber-50 hover:text-amber-700 flex items-center gap-2 transition-colors"
                    data-testid="ctx-market-fit"
                  >
                    <ChartBar size={16} className="text-amber-500" />
                    Market Fit Heatmap (3km)
                  </button>
                  <div className="border-t border-slate-100">
                    <button
                      onClick={() => setContextMenu(null)}
                      className="w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <ScoutPanel
                scoutCollapsed={scoutCollapsed} setScoutCollapsed={setScoutCollapsed}
                loadingScout={loadingScout}
                layerToggles={layerToggles} toggleLayer={toggleLayer}
                rentCircles={rentCircles} scoutLayers={scoutLayers}
                userCredits={userCredits} isAdmin={isAdmin}
              />

              {showMuniPanel && municipalityData && (
                <div className="absolute bottom-3 right-3 z-[1000] bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 p-4 w-72" style={{pointerEvents: 'auto'}} data-testid="municipality-panel">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{background: '#3b82f6'}}></div>
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">{municipalityData.name}</span>
                      {municipalityData.muni_type === 'metropolitan' && <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">Metro</span>}
                    </div>
                    <button onClick={() => { setShowMuniPanel(false); setMunicipalityData(null); }} className="text-slate-400 hover:text-slate-600 text-xs font-bold px-1">X</button>
                  </div>
                  <div className="text-xs text-slate-500 mb-2">{municipalityData.province}{municipalityData.district ? ` | ${municipalityData.district}` : ''} | Code: {municipalityData.code}</div>
                  {municipalityData.seat && <div className="text-xs text-slate-500 mb-2">Seat: {municipalityData.seat}</div>}

                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Population</span><span className="font-bold text-slate-700">{municipalityData.population?.toLocaleString()}</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Pop. Growth</span><span className="font-semibold text-slate-700">{municipalityData.pop_growth_pct}%</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Households</span><span className="font-semibold text-slate-700">{municipalityData.households?.toLocaleString()}</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Density</span><span className="font-semibold text-slate-700">{municipalityData.density_per_km2?.toLocaleString()}/km2</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Avg Income</span><span className="font-semibold text-slate-700">R{municipalityData.avg_household_income?.toLocaleString()}/mo</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Unemployment</span><span className="font-semibold text-red-600">{municipalityData.unemployment_rate}%</span></div>
                    <div className="flex justify-between py-1 border-b border-slate-100"><span className="text-slate-500">Youth (%)</span><span className="font-semibold text-slate-700">{municipalityData.youth_pct}%</span></div>
                    <div className="flex justify-between py-1"><span className="text-slate-500">Informal Dwellings</span><span className="font-semibold text-amber-600">{municipalityData.informal_dwelling_pct}%</span></div>
                  </div>

                  <div className="text-xs text-slate-400 mt-2 italic">Right-click on map for another area</div>
                </div>
              )}
            </div>

            {prediction?.comparables?.length > 0 && (
              <div className="comparables-panel" data-testid="comparables-panel">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                    <Buildings size={18} className="text-slate-600" />
                    Comparable Properties
                    <span className="text-xs font-normal text-slate-400 ml-1">5 nearest similar</span>
                  </h3>
                  <span className="text-xs text-slate-400">Sorted by similarity</span>
                </div>
                <div className="comparables-table-wrap" data-testid="comparables-table">
                  <table className="comparables-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Location</th>
                        <th>Bed</th>
                        <th>Bath</th>
                        <th>Amenities</th>
                        <th>Distance</th>
                        <th className="text-right">Rent</th>
                        <th className="text-right">Value</th>
                        <th className="text-right">vs Estimate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prediction.comparables.map((comp, idx) => {
                        const diff = ((comp.rent - prediction.estimated_rent) / prediction.estimated_rent * 100).toFixed(1);
                        const isHigher = diff > 0;
                        return (
                          <tr key={idx} data-testid={`comparable-row-${idx}`}>
                            <td className="comp-rank">{idx + 1}</td>
                            <td className="comp-city">{comp.city}</td>
                            <td>{comp.bedrooms}</td>
                            <td>{comp.bathrooms}</td>
                            <td className="comp-amenities">
                              {comp.has_security && <span className="comp-tag">Security</span>}
                              {comp.has_pool && <span className="comp-tag">Pool</span>}
                              {!comp.has_security && !comp.has_pool && <span className="text-slate-300">{"\u2014"}</span>}
                            </td>
                            <td>{comp.distance_km} km</td>
                            <td className="text-right font-semibold">{formatZAR(comp.rent)}</td>
                            <td className="text-right text-slate-500">{formatZAR(comp.rent * 175)}</td>
                            <td className={`text-right font-medium ${isHigher ? 'text-red-500' : 'text-emerald-600'}`}>
                              {isHigher ? '+' : ''}{diff}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {isAdmin && (
              <div className="variogram-panel-light" data-testid="variogram-panel">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">Spatial Variogram</h3>
                  <span className="text-xs uppercase tracking-wider text-slate-400">Semivariance vs Distance</span>
                </div>
                <div style={{ width: "100%", height: "160px" }}>
                  <ResponsiveContainer width="100%" height={160}>
                    <ScatterChart margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="distance" name="Distance" unit=" km" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} />
                      <YAxis dataKey="semivariance" name="Semivariance" tick={{ fontSize: 11, fill: "#64748b" }} axisLine={{ stroke: "#e2e8f0" }} />
                      <Tooltip cursor={{ strokeDasharray: "3 3" }} contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: 4, fontSize: 12 }} />
                      <ReferenceLine y={1.2} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: "Sill", fill: "#64748b", fontSize: 10 }} />
                      <Scatter name="Variogram" data={variogramData} fill="#1e3a5f" line={{ stroke: "#1e3a5f", strokeWidth: 2 }} />
                    </ScatterChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </>
        ) : activeTab === "spatial" ? (
          <SpatialTab scoutLayers={scoutLayers} />
        ) : (
          <ModelStatsTab
            prediction={prediction}
            coefficientData={coefficientData}
            variogramData={variogramData}
            oiModelFit={oiModelFit}
            fetchOiModelFit={fetchOiModelFit}
            isAdmin={isAdmin}
          />
        )}
      </main>

      {showAccountSettings && (
        <AccountSettingsModal
          user={user}
          credits={userCredits}
          onClose={() => setShowAccountSettings(false)}
          onCreditsRequested={fetchCredits}
        />
      )}

      {showUserGuide && (
        <UserGuidePage onClose={() => setShowUserGuide(false)} />
      )}
    </div>
  );
}

export default App;
